import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { Button } from './Button'
import { Modal } from './Modal'

export type ConfirmOptions = {
  title: string
  /** One or two sentences on what happens. Optional. */
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Red confirm button for removals and other things that are hard to undo. Default true. */
  danger?: boolean
}

type Api = { confirm: (opts: ConfirmOptions) => Promise<boolean> }
const Ctx = createContext<Api | null>(null)

/**
 * App-wide replacement for window.confirm: `const ok = await confirm({ title, body })`. One styled
 * dialog at a time, rendered once at the root so any component can ask without owning a modal.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((ok: boolean) => void) | null>(null)

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setPending(null)
  }, [])

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        resolver.current?.(false)
        resolver.current = resolve
        setPending(opts)
      }),
    [],
  )

  const api = useMemo<Api>(() => ({ confirm }), [confirm])
  const danger = pending?.danger ?? true
  return (
    <Ctx.Provider value={api}>
      {children}
      <Modal
        open={pending !== null}
        onClose={() => settle(false)}
        title={pending?.title ?? ''}
        footer={
          <>
            <Button variant="ghost" arrow={false} onClick={() => settle(false)}>{pending?.cancelLabel ?? 'Cancel'}</Button>
            <Button variant={danger ? 'danger' : 'primary'} arrow={false} autoFocus onClick={() => settle(true)}>{pending?.confirmLabel ?? 'Confirm'}</Button>
          </>
        }
      >
        {pending?.body ? <div className="text-small text-charcoal">{pending.body}</div> : null}
      </Modal>
    </Ctx.Provider>
  )
}

export function useConfirm(): Api['confirm'] {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>')
  return ctx.confirm
}
